import { Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { Auth0Service } from '../auth0.service';
import { WindowService } from '../../../../../common/angular-shared/services/window/window.service';
import { AMCAuth0ResponseUser } from '../../../../../common/js/authentication/Auth0Client';
import { AMCController } from '../../../../../common/js/authentication/amc/AMCController';

@Injectable()
export class AMCAuth0Service extends Auth0Service {
  private logoutUrl: string;
  private isAuth0Enabled?: boolean;

  public constructor(
    protected windowService: WindowService,
    protected route: ActivatedRoute,
    protected router: Router
  ) {
    super(windowService, route, router);
  }

  public getController() {
    return this.windowService.nativeWindow.controller;
  }

  public getAMCController(): AMCController {
    return this.getController().getAMCController();
  }

  // 02/12/20 - For now, wrapping in try/catch for safety because this
  // can be invoked through hit-snap-service (via BaseFormComponent)
  // and in that case, we will not have a real CP client controller.
  // The call will then fail because getAMCController() will not exist.
  // Need to revisit this with a better mechanism for protection.
  get isAMCheavyUser(): boolean {
    try {
      return this.getAMCController().isAMCheavyUser;
    } catch {
      return false;
    }
  }

  public async getAMCUserInfo(email?: string): Promise<AMCAuth0ResponseUser> {
    const isAuth0Enabled: boolean = await this.getIsAuth0Enabled();
    if (isAuth0Enabled) {
      const response: AMCAuth0ResponseUser = await this.getAMCController().getAMCUserInfo(email);
      return response;
    }
    return null;
  }

  public async createIdentityForCustomer(): Promise<void> {
    const isAuth0Enabled: boolean = await this.getIsAuth0Enabled();
    if (isAuth0Enabled) {
      await this.getAMCController().createIdentityForCustomer();
    }
  }

  public async handlePhoneRegistrationForMfa(phone?: string): Promise<void> {
    const isAuth0Enabled: boolean = await this.getIsAuth0Enabled();
    if (isAuth0Enabled) {
      await this.getController().handlePhoneRegistrationForMfa(phone);
    }
  }

  public async getIsAuth0Enabled(): Promise<boolean> {
    if (this.isAuth0Enabled === undefined) {
      this.isAuth0Enabled = await this.getController().getIsAuth0Enabled();
    }
    return this.isAuth0Enabled;
  }

  public async preLogout(): Promise<void> {
    await this.getAMCController().resetAmcUserInfo();
    this.logoutUrl = await this.getAMCController().getLogoutUrl();
  }

  public routeAfterLogout(): void {
    if (this.logoutUrl) {
      window.open(this.logoutUrl, '_self');
    }
  }

  // should take channel into consideration? do we care here?
  public async goToAuthenticate(): Promise<void> {
    const hasAMCaccount = this.getAMCController().hasAMCaccount;
    if (hasAMCaccount) {
      this.goToLogin();
    } else {
      this.goToRegister();
    }
  }

  public async whiteListAMCemail(): Promise<void> {
    await this.getAMCController().whiteListAMCemail();
  }

  // get PII for the heavy user
  public async getHeavyUserAMCdata(): Promise<void> {
    await this.getController().getHeavyUserAMCdata();
  }

  public async handleChangesForHeavyUserProps(prop?: string): Promise<void> {
    if (this.isAMCheavyUser) {
      await this.getController().handleChangesForHeavyUserProps();
    }
  }

  public async setAMCuserInfoForCustomer(): Promise<void> {
    await this.getController().setAMCuserInfoForCustomer();
  }
}
